import { Button, Dropdown, Space, message, notification } from "antd";
import { DownOutlined } from "@ant-design/icons";
import {
  ApiService,
  BaseRequestStatus,
  RequestStatus,
  SystemActionsEnum,
} from "../api";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ItemType } from "antd/lib/menu/hooks/useItems";
import {
  EditOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  HistoryOutlined,
  QrcodeOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { DOMAIN, NotificationType } from "../constants";
import { downloadFile } from "../api/downloadFile";
import HandoverModal from "./HandoverModal";
import ExtendModal from "./ExtendModal";
import AuditTrailModal from "./AuditTrailModal";
import QrCodeModal from "./QrCodeModal";

type ActionsDropdownProps = {
  id?: string;
  number?: string;
  status?: RequestStatus;
  //refreshRequest: () => void;
  fetch: number;
  setFetch: (fetch: number) => void;
};

const ActionsDropdown: React.FC<ActionsDropdownProps> = ({
  id,
  number,
  status,
  fetch,
  setFetch,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [actions, setActions] = useState<ItemType[]>([]);
  const [handoverModalVisible, setHandoverModalVisible] =
    useState<boolean>(false);
  const [extendModalVisible, setExtendModalVisible] = useState<boolean>(false);
  const [auditModalVisible, setAuditModalVisible] = useState<boolean>(false);
  const [qrModalVisible, setQrModalVisible] = useState<boolean>(false);

  // These endpoints are JWT-protected, so they can't be opened by navigation.
  const download = async (url: string, fallbackName: string) => {
    try {
      await downloadFile(url, fallbackName);
    } catch (error: any) {
      message.error(error?.message || t("Download failed"));
    }
  };

  const populateSystemActions = (status?: RequestStatus) => {
    const systemActions = Array.isArray(
      status?.system_actions && status.system_actions
    )
      ? (status?.system_actions as Array<SystemActionsEnum>) ?? []
      : [status?.system_actions];

    let requestActions: ItemType[] = [];
    systemActions.forEach((sa) => {
      switch (sa) {
        case SystemActionsEnum.EDIT:
          requestActions.push({
            label: t("Edit"),
            icon: <EditOutlined />,
            key: sa.toString(),
            onClick: () => navigate(`/requests/edit/${id}`),
          });
          break;
        case SystemActionsEnum.DOWNLOAD_DOCUMENT:
          requestActions.push({
            label: t("Download document"),
            key: sa.toString(),
            icon: <FileTextOutlined />,
            onClick: () =>
              download(
                `${DOMAIN}/api/main/download-document/${id}/`,
                `${number ?? id}.docx`,
              ),
          });
          break;
        case SystemActionsEnum.DOWNLOAD_CMS:
          requestActions.push({
            label: t("Download CMS"),
            key: sa.toString(),
            icon: <FileDoneOutlined />,
            onClick: () =>
              download(
                `${DOMAIN}/api/main/get-request-cms/${id}/`,
                `${number ?? id}.cms`,
              ),
          });
          break;
        case SystemActionsEnum.HANDOVER:
          requestActions.push({
            label: t("Handover"),
            key: sa.toString(),
            onClick: () => setHandoverModalVisible(true),
          });
          break;
        case SystemActionsEnum.EXTEND:
          requestActions.push({
            label: t("Extend"),
            key: sa.toString(),
            onClick: () => setExtendModalVisible(true),
          });
          break;
      }
    });

    return requestActions;
  };

  const handlePerformAction = async (actionId: number) => {
    try {
      await ApiService.apiMainPerformActionCreate({
        request_id: parseInt(id!),
        status_id: actionId,
      });
      openNotificationWithIcon("success");
      setFetch(fetch + 1);
    } catch (e) {
      openNotificationWithIcon("error");
    }
  };

  const openNotificationWithIcon = (type: NotificationType) => {
    if (type === "success") {
      notification[type]({
        message: t("Success"),
        description: t("Request has been successfully processed."),
      });
    } else {
      notification[type]({
        message: t("Error"),
        description: t("Something went wrong."),
      });
    }
  };

  const populateOtherActions = (status?: RequestStatus) => {
    const otherActions: Array<any> = status?.next_statuses ?? [];

    let statusActions: ItemType[] = [];
    otherActions.forEach((action: BaseRequestStatus) => {
      statusActions.push({
        label: action.action_title,
        key: action.id,
        onClick: () => handlePerformAction(action.id),
      });
    });

    return statusActions;
  };

  const auditAction: ItemType = {
    label: t("Audit Trail"),
    key: "audit-trail",
    icon: <HistoryOutlined />,
    onClick: () => setAuditModalVisible(true),
  };

  const qrAction: ItemType = {
    label: t("Generate QR"),
    key: "generate-qr",
    icon: <QrcodeOutlined />,
    onClick: () => setQrModalVisible(true),
  };

  useEffect(() => {
    setActions([
      qrAction,
      auditAction,
      ...populateSystemActions(status),
      ...populateOtherActions(status),
    ]);
  }, [status]);

  if (status === undefined) {
    return <></>;
  }

  return (
    <>
      <Dropdown menu={{ items: actions }}>
        <Button type="primary">
          <Space>
            {t("Actions")}
            <DownOutlined />
          </Space>
        </Button>
      </Dropdown>
      <HandoverModal
        fetch={fetch}
        setFetch={setFetch}
        requestId={id!}
        visible={handoverModalVisible}
        setVisible={setHandoverModalVisible}
      />
      <ExtendModal
        fetch={fetch}
        setFetch={setFetch}
        requestId={id!}
        visible={extendModalVisible}
        setVisible={setExtendModalVisible}
      />
      <AuditTrailModal
        requestId={id}
        visible={auditModalVisible}
        setVisible={setAuditModalVisible}
      />
      <QrCodeModal
        requestId={id}
        number={number}
        visible={qrModalVisible}
        setVisible={setQrModalVisible}
      />
    </>
  );
};

export default ActionsDropdown;
